import './Annotation.css'
import React, { useState, useRef, useEffect } from 'react'
import classnames from 'classnames'
import formatMessage from 'format-message'
import CommentCreateButton from '../comments/CommentCreateButton'
import CommentLine from '../comments/CommentLine'
import CommentsContainer from '../comments/CommentsContainer'
import Tooltip from './Tooltip'
import GenericPopup from '../GenericPopup'
import {
  annotationHasComment,
  focusOnNextAnnotation,
  getCommentCreateButtonVisibility,
  getCommentLineVisibility,
  getRelatedTargetOrEquivalent,
  getTranslatedCordinates,
  scrollIntoView,
} from './utils'
import { isPenEvent, isTouchEvent } from '../utils'
import { ANNOTATION_TYPES_WITH_AUTO_EXPANDED_COMMENT, ANNOTATION_TAP_THRESHOLD } from '../constants'

class Annotation_ extends React.Component {
  componentDidCatch(error, info) {
    this.props.setHasError(true)
    console.error('Error caught in Annotation', { error, info })
  }

  handleMouseDown = (event) => {
    if (isPenEvent(event)) {
      this.props.onIsPenAnnotatingChange(true)
    }
    this.props.onTextSelectionDisable()
    this.props.originalPageX.current = event.pageX
    this.props.originalPageY.current = event.pageY
    this.props.setIsAnnotationFocused(true)
    this.props.setIsMouseDown(true)
    document.addEventListener('pointerup', this.handleDocumentMouseUp)
    document.addEventListener('pointermove', this.handleDocumentMouseMove)
  }

  handleDocumentMouseMove = (event) => {
    window.cancelAnimationFrame(this.props.moveRequest.current)
    this.props.moveRequest.current = window.requestAnimationFrame(
      this.__handleDocumentMouseMove.bind(this, event),
    )
  }

  __handleDocumentMouseMove = (event) => {
    if (
      this.props.isMovable === false ||
      this.props.isMouseDown === false ||
      this.props.canEdit === false
    )
      return

    const { translateX, translateY } = getTranslatedCordinates({
      pageX: event.pageX,
      pageY: event.pageY,
      originalPageX: this.props.originalPageX.current,
      originalPageY: this.props.originalPageY.current,
      viewportHeight: this.props.viewportHeight,
      viewportWidth: this.props.viewportWidth,
      x: this.props.x,
      y: this.props.y,
      height: this.props.height,
      width: this.props.width,
    })

    if (this.props.isDragging === false) {
      this.props.onDraggingStart(this.props.id)
    }

    this.props.setIsDragging(true)
    this.props.setTranslateX(translateX)
    this.props.setTranslateY(translateY)
  }

  handleDocumentMouseUp = () => {
    document.removeEventListener('pointerup', this.handleDocumentMouseUp)
    document.removeEventListener('pointermove', this.handleDocumentMouseMove)
    this.props.handleDocumentMouseUp()
  }

  render() {
    const annotation = this.props.annotation
    const containerClassNames = classnames(
      'Annotation-svg-container',
      this.props.containerClassName,
    )
    const svgClasses = classnames('Annotation-svg', this.props.className, {
      'Annotation-svg--focused': this.props.isAnnotationFocused,
      'is-dragging': this.props.isDragging,
    })
    if (!(annotation.rect instanceof Array)) {
      throw new Error('Annotation is missing rect property')
    }
    const rect = annotation.rect.map((point) => {
      const [x, y] = point
      return [x + this.props.translateX, y + this.props.translateY]
    })

    const x = this.props.x + this.props.translateX - this.props.selectionBoxPadding
    const y = this.props.y + this.props.translateY - this.props.selectionBoxPadding

    const annotationWidth = this.props.width + 2 * this.props.selectionBoxPadding
    const annotationHeight = this.props.height + 2 * this.props.selectionBoxPadding

    const hasComments = this.props.commentReplies && this.props.commentReplies.length > 0

    const differentRootCommentAuthor =
      hasComments && annotation.user_id !== this.props.commentReplies[0].user_id

    const displayAnnotationToolbar =
      this.props.isCommentsContainerFocused === false &&
      (!hasComments || this.props.canEdit || this.props.canDelete || differentRootCommentAuthor) &&
      !annotation.deleted
    const annotationClasses = classnames('Annotation', {
      'Annotation--selected': this.props.isSelected,
      'Annotation--error': this.props.hasError,
      'Annotation--is-dragging': this.props.isDragging,
    })

    if (this.props.hasError) {
      return (
        <div
          className="Annotation--internal"
          style={{
            backgroundColor: 'red',
            color: 'white',
            fontSize: '0.7rem',
            height: annotationHeight,
            left: x,
            position: 'absolute',
            top: y,
            width: annotationWidth,
          }}
        >
          {formatMessage('Error rendering annotation')}
        </div>
      )
    }

    const hasComment = annotationHasComment(this.props.commentReplies)

    const isCommentable = this.props.isAnnotatable || this.props.canEdit

    const showCommentCreateButton = getCommentCreateButtonVisibility(
      hasComment,
      isCommentable,
      this.props.isCommenting,
      this.props.isSelected,
      annotation.type,
      annotation.deleted,
    )

    const showCommentLine = getCommentLineVisibility(
      this.props.isDragging,
      showCommentCreateButton,
      this.props.isCommenting,
      hasComment || annotation.deleted,
      this.props.isHovered,
      this.props.isSelected,
    )

    const showSecondaryControls = this.props.shouldShowSecondaryControls()

    const showCommentsContainer =
      (showCommentCreateButton === false && annotation.type !== 'freetext') || annotation.deleted

    const deleteMessage = this.props.annotation.contents
      ? formatMessage(
          'Are you sure you want to delete this annotation and all its related comments and replies?',
        )
      : formatMessage('Are you sure you want to delete this annotation?')

    return (
      <div
        className={annotationClasses}
        id={`Annotation-${this.props.id}`}
        onClick={this.props.handleClick}
        onFocus={this.props.handleAnnotationFocus}
        onBlur={this.props.handleFocusOut}
        onKeyDown={this.props.handleTabPressed}
        onKeyUp={this.props.handleTabReleased}
        onMouseEnter={this.props.handleMouseEnter}
        onMouseLeave={this.props.handlePointerLeave}
        onPointerEnter={this.props.handlePointerEnter}
        onPointerLeave={this.props.handlePointerLeave}
        onPointerMove={this.props.handlePointerMove}
        ref={this.props.div}
        tabIndex={-1}
      >
        {this.props.isDeletePopupVisible && (
          <GenericPopup
            message={deleteMessage}
            onCancel={this.props.handleDeletePopupCancel}
            onConfirm={this.props.handleDeletePopupConfirm}
            severity="warning"
          />
        )}
        <div
          className="Annotation--internal"
          role="text"
          aria-label={`Coordinates are x, ${Math.floor(x)} and y, ${Math.floor(y)}`}
          style={{
            left: x,
            top: y,
            width: annotationWidth,
            height: annotationHeight,
            position: 'absolute',
          }}
        >
          <div
            className={containerClassNames}
            onBlur={this.props.handleSvgContainerBlur}
            onFocus={this.props.handleSvgContainerFocus}
            onKeyDown={this.props.handleKeyDown}
            onKeyUp={this.handleDocumentMouseUp}
            onPointerDown={this.handleMouseDown}
            onPointerUp={this.props.handlePointerUp}
            onPointerLeave={this.props.handlePointerLeave}
            ref={this.props.svgContainer}
            tabIndex={0}
          >
            <svg
              aria-selected={this.props.isSelected}
              aria-label={this.props.title}
              className={svgClasses}
              ref={this.props.svg}
              style={{
                width: annotationWidth,
                height: annotationHeight,
              }}
            >
              {this.props.render(this.props.isSvgContainerFocused)}
            </svg>
          </div>
          {this.props.isSelected && displayAnnotationToolbar && (
            <div className="AnnotationToolbar-container">
              <Tooltip
                authorName={annotation.user_name}
                canEdit={this.props.canEdit}
                canDelete={this.props.canDelete}
                displayAuthorName={
                  (hasComment === false &&
                    annotation.user_id !== this.props.sessionUserObject.user_id) ||
                  differentRootCommentAuthor
                }
                displayCreateAnnotation={
                  this.props.multiStrokeAnnotationId === annotation.id ||
                  annotation?.savedFromSession
                }
                onCreateAnnotation={this.props.handleCheckmarkClick}
                onDeleteAnnotation={this.props.handleAnnotationDelete}
                isSavedFromSession={annotation?.savedFromSession}
              />
            </div>
          )}
        </div>

        {showCommentLine && (
          <CommentLine
            annotationId={annotation.id}
            currentScrollTop={this.props.currentScrollTop}
            isAutomaticallyScrollingComments={this.props.isAutomaticallyScrollingComments}
            isSelected={this.props.isSelected}
            key={annotation.id}
            managedTop={this.props.managedTop}
            onSelectAnnotation={this.props.onSelectAnnotation}
            pageHeight={this.props.pageHeight}
            rect={annotation.rect}
            viewportWidth={this.props.viewportWidth}
            widthExtension={showCommentCreateButton ? 16 : 24}
          />
        )}

        <CommentCreateButton
          annotationId={annotation.id}
          isVisible={showCommentCreateButton}
          left={this.props.viewportWidth}
          top={annotation.rect[1][1] + this.props.translateY}
          onCreateComment={this.props.handleCreateComment}
        />

        {showCommentsContainer &&
          this.props.commentsContainerRef &&
          this.props.commentsContainerRef.current && (
            <CommentsContainer
              annotation={annotation}
              annotationId={this.props.id || annotation.id}
              canDelete={this.props.canDelete}
              canEdit={this.props.canEdit}
              commentReplies={this.props.commentReplies}
              commentsContainerRef={this.props.commentsContainerRef}
              shouldCommentGroupStack={this.props.shouldCommentGroupStack}
              iconColor={this.props.iconColor}
              isAnnotatable={this.props.isAnnotatable}
              isCommenting={this.props.isCommenting}
              isSelected={this.props.isSelected}
              isVisible={this.props.isVisible}
              managedTop={this.props.managedTop}
              onCommentBlur={this.props.handleCommentsContainerBlur}
              onCommentEscape={this.props.handleCommentEscape}
              onCommentFocus={this.props.handleCommentsContainerFocus}
              onCommentGroupBlur={this.props.handleCommentGroupBlur}
              onCommentGroupHeightChange={this.props.onCommentGroupHeightChange}
              onCommentGroupUnmount={this.props.onCommentGroupUnmount}
              onCommentSave={this.props.onCommentSave}
              onDeleteAnnotation={this.props.handleCommentDelete}
              onDeletePopupCancel={this.props.handleDeletePopupCancel}
              onDeletePopupConfirm={this.props.handleDeletePopupCancel}
              onHoverAnnotation={this.props.onHoverAnnotation}
              onSelectComment={this.props.setCommentState}
              rect={rect}
              sessionUserObject={this.props.sessionUserObject}
            />
          )}
        <div
          className="SecondaryControlsContainer"
          style={{
            width: showSecondaryControls ? 'auto' : '0px',
          }}
        >
          {this.props.secondaryControls}
        </div>
      </div>
    )
  }
}

export default function Annotation(props) {
  const div = useRef(null)
  const svgContainer = useRef(null)
  const svg = useRef(null)
  const originalPageX = useRef(null)
  const originalPageY = useRef(null)
  const moveRequest = useRef(null)

  const [hasError, setHasError] = useState(false)
  const [isAnnotationFocused, setIsAnnotationFocused] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false)
  const [isSvgContainerFocused, setIsSvgContainerFocused] = useState(false)
  const [isTabPressed, setIsTabPressed] = useState(false)
  const [translateX, setTranslateX] = useState(0) // for dragging
  const [translateY, setTranslateY] = useState(0)
  const [isCommenting, setIsCommenting] = useState(
    props.isSelected && ANNOTATION_TYPES_WITH_AUTO_EXPANDED_COMMENT.includes(props.annotation.type),
  )
  const [isCommentsContainerFocused, setIsCommentsContainerFocused] = useState(
    props.isSelected && ANNOTATION_TYPES_WITH_AUTO_EXPANDED_COMMENT.includes(props.annotation.type),
  )

  const handleMouseEnter = (_event) => {
    props.onHoverAnnotation(props.id)
  }

  const handlePointerEnter = (_event) => {
    // Handle touch/pointer events entering annotation (same as mouse enter)
    props.onHoverAnnotation(props.id)
  }

  const handlePointerLeave = (event) => {
    props.onHoverAnnotation(null)
    if (isPenEvent(event)) {
      props.onIsPenAnnotatingChange(false)
    }
  }

  const handleAnnotationSave = (event) => {
    if (event instanceof Event) {
      event.stopPropagation()
    }
    if (typeof props.onMultiStrokeAnnotation === 'function') {
      props.onMultiStrokeAnnotation(null)
    }
    props.onCreateAnnotation(props.viewportHeight, props.viewportScale, props.annotation)
  }

  const handleCommentDelete = (annotationId, isRootComment = false) => {
    setIsCommenting(false)
    props.onIsPenAnnotatingChange(false)
    props.onDeleteAnnotation(props.pageNumber, annotationId, isRootComment)
    svgContainer.current.focus()
    props.onHoverAnnotation(null)
  }

  const shouldShowSecondaryControls = () => {
    return props.isSelected && props.canEdit && props.secondaryControls
  }

  const handlePointerMove = (event) => {
    if (isPenEvent(event)) {
      props.onIsPenAnnotatingChange(true)
    }
  }

  const handlePointerUp = (event) => {
    if (isPenEvent(event)) {
      props.onIsPenAnnotatingChange(false)
    }
  }

  const handleCommentGroupBlur = (event) => {
    props.onIsPenAnnotatingChange(false)
    const element = getRelatedTargetOrEquivalent(event)
    const isElementInsideAnnotation =
      div.current &&
      (div.current.contains(element) ||
        (props.commentsContainerRef &&
          props.commentsContainerRef.current &&
          props.commentsContainerRef.current.contains(element)))

    if (isElementInsideAnnotation === false) {
      setIsAnnotationFocused(false)
      setIsCommenting(false)
      setIsCommentsContainerFocused(false)
      props.onDeselectAnnotation(props.id)
    }
  }

  const handleTabReleased = (event) => {
    if (event.key !== 'Tab') {
      return
    }
    setIsTabPressed(false)
  }

  const setCommentState = (isCommenting) => {
    props.onIsCommentingChange({ isCommenting })
    setIsCommenting(isCommenting)
  }

  const handleCreateComment = () => {
    if (props.multiStrokeAnnotationId) {
      handleAnnotationSave()
    }
    setCommentState(true)
  }

  const handleTabPressed = (event) => {
    if (event.key !== 'Tab') {
      return
    }
    if (event.persist) {
      event.persist()
    }
    setIsTabPressed(true)

    const isTabPressed = true
    const shiftAndTabPressed = isTabPressed && props.isShiftPressed

    // handle Shift + Tab reverse traversing out of the comment and into the given annotation.
    if (shiftAndTabPressed) {
      const firstDeleteCommentEntryButton = props.commentsContainerRef.current.querySelector(
        '.Delete-comment-entry-button',
      )

      const commentGroup = document.querySelector(`#CommentGroup-${props.id}`)

      const isFirstDeleteCommentEntryButtonSelected =
        firstDeleteCommentEntryButton === document.activeElement

      const isEntireCommentGroupSelected = commentGroup === document.activeElement

      const isTextareaSelected = document.activeElement.classList.contains('CommentGroup-textarea')

      // simulates reverse tabbing for users that don't have delete access.
      if (
        isTextareaSelected &&
        commentGroup &&
        props.canDelete === false &&
        props.annotation.deleted == null
      ) {
        const textareas = Array.from(commentGroup.querySelectorAll('textarea'))
        const aboveTextareaIndex =
          textareas.findIndex((node) => document.activeElement === node) - 1
        if (aboveTextareaIndex >= 0) {
          textareas[aboveTextareaIndex].focus()
          event.preventDefault()
          event.stopPropagation()
          return
        }
      }

      const isFirstTextareaSelectedAndUserCannotDelete =
        commentGroup &&
        commentGroup.querySelector('textarea') === document.activeElement &&
        props.canDelete === false

      if (
        isFirstDeleteCommentEntryButtonSelected ||
        isEntireCommentGroupSelected ||
        (isFirstTextareaSelectedAndUserCannotDelete && props.annotation.deleted == null)
      ) {
        event.preventDefault()
        event.stopPropagation()
        div.current.querySelector('.Annotation-svg-container').focus()
      }
    } else if (isTabPressed) {
      // handle Tab traversing into the comment body.
      const isDeleteAnnotationButtonSelected =
        document.activeElement.getAttribute('aria-label') === formatMessage('delete annotation')

      const commentReplyButtons =
        props.commentsContainerRef.current.querySelectorAll('.Comment-reply-button')
      const lastCommentReplyButton = commentReplyButtons[commentReplyButtons.length - 1]

      const isLastCommentReplyButtonSelected = lastCommentReplyButton === document.activeElement

      const hasComment = annotationHasComment(props.commentReplies)

      const isCommentable = props.isAnnotatable || props.canEdit

      const showCommentCreateButton = getCommentCreateButtonVisibility(
        hasComment,
        isCommentable,
        isCommenting,
        props.isSelected,
        props.annotation.type,
        props.annotation.deleted,
      )

      const isAnnotationSelectedAndUserCannotDelete =
        document.activeElement.classList.contains('Annotation-svg-container') &&
        props.canDelete === false

      const shouldFocusOnCommentGroup =
        (isDeleteAnnotationButtonSelected || isAnnotationSelectedAndUserCannotDelete) &&
        showCommentCreateButton === false

      const hasNextAnnotation =
        props.ySortedStagedRootAnnotations[props.ySortedStagedRootAnnotations.length - 1].id !==
        props.id

      if (shouldFocusOnCommentGroup) {
        props.commentsContainerRef.current.querySelector('.CommentGroup').focus()
      } else if (isLastCommentReplyButtonSelected && props.canEdit) {
        div.current.querySelector('.SecondaryControls').focus()
      } else if (isLastCommentReplyButtonSelected && hasNextAnnotation && props.canEdit === false) {
        // simulates tabbing to a new annotation when user doesn't have edit access.
        event.preventDefault()
        event.stopPropagation()
        focusOnNextAnnotation(props.ySortedStagedRootAnnotations, props.id)
      }
    }
  }

  const handleCommentEscape = () => {
    svgContainer.current.focus()
    setIsCommenting(false)
  }

  const handleClick = (_event) => {
    setIsAnnotationFocused(true)
    props.onSelectAnnotation(props.id)
  }

  const handleDeletePopupConfirm = (event) => {
    setIsDeletePopupVisible(false)
    props.onIsPenAnnotatingChange(false)
    event.preventDefault()
    event.stopPropagation()
    if (!props.canDelete) {
      return
    }
    props.onMultiStrokeAnnotation && props.onMultiStrokeAnnotation(null)
    props.onTextSelectionEnable()
    props.onDeleteAnnotation(props.pageNumber, props.id)
  }

  const handleSvgContainerFocus = (event) => {
    event.preventDefault()
    setIsSvgContainerFocused(true)
    props.onSelectAnnotation(props.id)
  }

  const handleSvgContainerBlur = (_event) => {
    setIsSvgContainerFocused(false)
    props.onTextSelectionEnable()
  }

  const handleCommentsContainerFocus = (_event) => {
    setIsCommentsContainerFocused(true)
  }

  const handleCommentsContainerBlur = (_event) => {
    setIsCommentsContainerFocused(false)
  }

  const handleCheckmarkClick = (event) => {
    if (svgContainer.current instanceof HTMLElement) {
      svgContainer.current.focus()
    }
    handleAnnotationSave(event)
  }

  const handleDeletePopupCancel = (_event) => {
    setIsDeletePopupVisible(false)
    props.onIsPenAnnotatingChange(false)
    if (svgContainer.current instanceof HTMLElement) {
      svgContainer.current.focus()
      props.onHoverAnnotation(null)
    }
  }

  const handleAnnotationDelete = () => {
    if (props.canDelete) {
      setIsDeletePopupVisible(true)
    }
  }

  const handleAnnotationFocus = (event) => {
    event.preventDefault()
    setIsAnnotationFocused(true)
    props.onSelectAnnotation(props.id)

    setTimeout(() => {
      if (div.current instanceof HTMLElement) {
        const ensureThatElementIsInView = () => {
          if (!(div.current instanceof HTMLElement)) return
          const commentGroupDiv = div.current.querySelector('.CommentGroup--active')

          if (commentGroupDiv instanceof HTMLElement) {
            const { top } = commentGroupDiv.getBoundingClientRect()

            if (top < 90) {
              scrollIntoView(commentGroupDiv)
            }
          }
        }

        setTimeout(ensureThatElementIsInView, 500)

        setTimeout(ensureThatElementIsInView, 750)
      }
    }, 5)
  }

  const move = (x, y) => {
    if (props.isMovable === false) return
    const newX = props.x + translateX + x
    const newY = props.y + translateY + y
    const isValidX = newX >= 0 && newX + props.width < props.viewportWidth
    const isValidY = newY >= 0 && newY + props.height < props.viewportHeight
    setTranslateX(isValidX ? translateX + x : translateX)
    setTranslateY(isValidY ? translateY + y : translateY)
  }

  const handleFocusOut = (event) => {
    if (event.persist) {
      event.persist()
    }

    const relatedTarget = getRelatedTargetOrEquivalent(event)

    const shiftAndTabPressed = isTabPressed && props.isShiftPressed
    setIsTabPressed(false)

    const isRelatedTargetInsideAnnotation =
      div.current.contains(relatedTarget) ||
      (props.commentsContainerRef &&
        props.commentsContainerRef.current &&
        props.commentsContainerRef.current.contains(relatedTarget))

    // handle tabbing out of deleted annotations.
    if (
      isTabPressed &&
      shiftAndTabPressed === false &&
      isRelatedTargetInsideAnnotation === false &&
      props.annotation.deleted
    ) {
      setIsAnnotationFocused(false)
      setIsCommenting(false)
      props.onDeselectAnnotation(props.id)
      focusOnNextAnnotation(props.ySortedStagedRootAnnotations, props.id)
      return
    }

    const firstAcknowledgeDeleteButton =
      props.commentsContainerRef &&
      props.commentsContainerRef.current &&
      props.commentsContainerRef.current.querySelector('.Acknowledge-remove-deleted-comment-button')

    const isFirstAcknowledgeDeleteButtonSelected = firstAcknowledgeDeleteButton === event.target

    // handle reverse tabbing out of deleted annotations.
    if (
      shiftAndTabPressed &&
      isRelatedTargetInsideAnnotation === false &&
      isFirstAcknowledgeDeleteButtonSelected
    ) {
      event.preventDefault()
      event.stopPropagation()
      div.current.querySelector('.Annotation-svg-container').focus()
      return
    }

    const isSecondaryControlsVisible = shouldShowSecondaryControls()

    const eventOriginatedFromSecondaryToolbar =
      isSecondaryControlsVisible &&
      div.current.querySelector('.SecondaryControls').contains(event.target)

    const didTabFromSecondaryToolbar = eventOriginatedFromSecondaryToolbar && isTabPressed

    const eventOriginatedFromCommentCreateButton =
      event.target.className?.includes('Comment-create')

    const eventOriginatedFromCommentReplyButton =
      event.target.className?.includes('Comment-reply-button')

    const isRelatedTargetInsideViewerControls =
      relatedTarget &&
      relatedTarget.className &&
      relatedTarget.className?.includes('AnnotationControlButton')

    if (
      didTabFromSecondaryToolbar === false &&
      isRelatedTargetInsideAnnotation === false &&
      (eventOriginatedFromCommentCreateButton === false ||
        isRelatedTargetInsideViewerControls === true) &&
      eventOriginatedFromCommentReplyButton === false
    ) {
      setIsAnnotationFocused(false)
      setIsCommenting(false)
      props.onDeselectAnnotation(props.id)
      if (shiftAndTabPressed === false) {
        return
      }
    }

    // check if we should Shift + Tab reverse traverse into the comment from the Secondary Toolbar.
    const isSecondaryControlsBluring =
      isSecondaryControlsVisible &&
      div.current.querySelector('.SecondaryControls').contains(event.target) &&
      div.current.querySelector('.SecondaryControls').contains(relatedTarget) === false

    if (isSecondaryControlsBluring && shiftAndTabPressed) {
      event.preventDefault()
      event.stopPropagation()

      const replyButtons =
        props.commentsContainerRef.current.querySelectorAll('.Comment-reply-button')

      const lastReplyButton = replyButtons[replyButtons.length - 1]

      lastReplyButton && lastReplyButton.focus()
      return
    }

    // check if we should Shift + Tab reverse traverse into the above comment textarea.
    if (isRelatedTargetInsideAnnotation === false && shiftAndTabPressed) {
      const annotationIndex = props.ySortedStagedRootAnnotations.findIndex((annotation) => {
        return props.id === annotation.id
      })
      if (annotationIndex > 0) {
        const annotation = props.ySortedStagedRootAnnotations[annotationIndex - 1]

        const commentGroup = document.querySelector(`#CommentGroup-${annotation.id}`)
        if (commentGroup != null) {
          if (annotation.type === 'freetext' && annotation.deleted) {
            commentGroup.querySelector('.Acknowledge-remove-deleted-comment-button').focus()
            return
          }

          const commentGroupTextareas = commentGroup.querySelectorAll('textarea')

          const lastCommentGroupTextarea = commentGroupTextareas[commentGroupTextareas.length - 1]

          lastCommentGroupTextarea.focus()

          return
        } else {
          event.preventDefault()
          if (annotation.type === 'freetext') {
            document.querySelector(`#Annotation-${annotation.id} textarea`).focus()
          } else {
            document.querySelector(`#Annotation-${annotation.id} .Annotation-svg-container`).focus()
          }
          return
        }
      }
    }

    // check if this is the last annotation & whether we should transfer focus to the next page

    const isLastAnnotation =
      props.ySortedStagedRootAnnotations[props.ySortedStagedRootAnnotations.length - 1].id ===
      props.id

    const focusIsLeavingUnownedComment =
      eventOriginatedFromCommentCreateButton && props.canEdit === false

    const shouldTransferFocusToNextPage =
      isLastAnnotation &&
      (isSecondaryControlsBluring || focusIsLeavingUnownedComment) &&
      props.isShiftPressed === false &&
      isTabPressed

    const isFirstAnnotation = props.ySortedStagedRootAnnotations[0].id === props.id

    const shouldTransferFocusToPreviousPage =
      isFirstAnnotation &&
      shiftAndTabPressed &&
      event.target.className?.includes('Annotation-svg-container')

    if (shouldTransferFocusToNextPage) {
      // notify Viewer.js that we need to transfer focus to the next page.
      event.preventDefault()
      event.stopPropagation()
      setIsAnnotationFocused(false)
      setIsCommenting(false)
      props.onDeselectAnnotation(props.id)
      props.handleNextPageFocus()
      return
    } else if (shouldTransferFocusToPreviousPage) {
      event.preventDefault()
      event.stopPropagation()
      setIsCommenting(false)
      setIsAnnotationFocused(false)
      props.onDeselectAnnotation(props.id)
      props.handlePreviousPageFocus()
    }
  }

  const handleKeyDown = (event) => {
    if (!(svg.current instanceof SVGElement)) {
      return
    }
    if (props.isMovable && props.canEdit) {
      switch (event.key) {
        case 'ArrowRight':
          move(event.shiftKey ? 1 : 5, 0)
          event.preventDefault()
          return
        case 'ArrowLeft':
          move(event.shiftKey ? -1 : -5, 0)
          event.preventDefault()
          return
        case 'ArrowUp':
          move(0, event.shiftKey ? -1 : -5)
          event.preventDefault()
          return
        case 'ArrowDown':
          move(0, event.shiftKey ? 1 : 5)
          event.preventDefault()
          return
        default:
          break
      }
    }

    switch (event.key) {
      case 'Delete':
      case 'Backspace':
        handleAnnotationDelete()
        // TODO: focus on next annotation if available
        return
      case 'Escape':
        svg.current.blur()
        break
      default:
        break
    }
  }

  const handleDocumentMouseUp = () => {
    if (isDragging === false) {
      setIsMouseDown(false)
      props.onDraggingEnd()
      return
    }

    props.onTextSelectionEnable()
    if (isDragging) {
      setIsDragging(false)
      setIsMouseDown(false)
      if (props.onMoveAnnotation && (translateX !== 0 || translateY !== 0)) {
        // In order to preserve the ability to "right" click on an annotation with the pen (by pressing the action button and tapping), but
        // not trigger the right-click context menu when an annotation is moved we check to see how far the annotation moved.
        // If its movement is below a given "tap" threshold we consider the annotation "tapped" and show the right-click context menu,
        // otherwise we block the context menu until the annotation has finished moving.
        const annotationWasTapped =
          Math.abs(translateX) <= ANNOTATION_TAP_THRESHOLD &&
          Math.abs(translateY) <= ANNOTATION_TAP_THRESHOLD
        if (annotationWasTapped === false) {
          if (props.onBlockContextMenu) {
            props.onBlockContextMenu(true)
          }
        }

        props.onMoveAnnotation(props.annotation, translateX, translateY)
      }
      props.onDraggingEnd()
    }
  }

  useEffect(() => {
    div.current.addEventListener('focusout', handleFocusOut, true)
    return () => {
      if (div.current instanceof HTMLElement) {
        div.current.removeEventListener('focusout', handleFocusOut, true)
      }
    }
  }, [])

  useEffect(() => {
    setTranslateX(0)
    setTranslateY(0)
  }, [props.x, props.y])

  useEffect(() => {
    if (isCommenting === false) {
      props.onHoverAnnotation(null)
    }
  }, [isCommenting])

  useEffect(() => {
    if (props.isSelected === false) {
      props.onIsCommentingChange({ isCommenting: false })
      setIsAnnotationFocused(false)
      setIsCommenting(false)
      setIsCommentsContainerFocused(false)
    }
  }, [props.isSelected])

  useEffect(() => {
    if (isCommenting) {
      props.onIsCommentingChange({ isCommenting: true })
    }
  }, [])

  return (
    <Annotation_
      {...props}
      div={div}
      handleMouseEnter={handleMouseEnter}
      handlePointerEnter={handlePointerEnter}
      handlePointerLeave={handlePointerLeave}
      hasError={hasError}
      isAnnotationFocused={isAnnotationFocused}
      isCommenting={isCommenting}
      isCommentsContainerFocused={isCommentsContainerFocused}
      isDeletePopupVisible={isDeletePopupVisible}
      isDragging={isDragging}
      isMouseDown={isMouseDown}
      isSvgContainerFocused={isSvgContainerFocused}
      isTabPressed={isTabPressed}
      setHasError={setHasError}
      setIsAnnotationFocused={setIsAnnotationFocused}
      setIsCommenting={setIsCommenting}
      setIsCommentsContainerFocused={setIsCommentsContainerFocused}
      setIsDeletePopupVisible={setIsDeletePopupVisible}
      setIsDragging={setIsDragging}
      setIsMouseDown={setIsMouseDown}
      setIsSvgContainerFocused={setIsSvgContainerFocused}
      setIsTabPressed={setIsTabPressed}
      setTranslateX={setTranslateX}
      setTranslateY={setTranslateY}
      svg={svg}
      svgContainer={svgContainer}
      translateX={translateX}
      translateY={translateY}
      handleAnnotationSave={handleAnnotationSave}
      handleCommentDelete={handleCommentDelete}
      shouldShowSecondaryControls={shouldShowSecondaryControls}
      handlePointerMove={handlePointerMove}
      handlePointerUp={handlePointerUp}
      handleCommentGroupBlur={handleCommentGroupBlur}
      handleTabReleased={handleTabReleased}
      setCommentState={setCommentState}
      handleCreateComment={handleCreateComment}
      handleTabPressed={handleTabPressed}
      handleCommentEscape={handleCommentEscape}
      handleClick={handleClick}
      handleDeletePopupConfirm={handleDeletePopupConfirm}
      handleSvgContainerFocus={handleSvgContainerFocus}
      handleSvgContainerBlur={handleSvgContainerBlur}
      handleCommentsContainerFocus={handleCommentsContainerFocus}
      handleCommentsContainerBlur={handleCommentsContainerBlur}
      handleCheckmarkClick={handleCheckmarkClick}
      handleDeletePopupCancel={handleDeletePopupCancel}
      handleAnnotationDelete={handleAnnotationDelete}
      originalPageX={originalPageX}
      originalPageY={originalPageY}
      handleAnnotationFocus={handleAnnotationFocus}
      move={move}
      moveRequest={moveRequest}
      handleFocusOut={handleFocusOut}
      handleKeyDown={handleKeyDown}
      handleDocumentMouseUp={handleDocumentMouseUp}
    />
  )
}
